// 261005 E1-P3b — hàm THUẦN của chặng ④ Chia tập (2 thẻ chế độ). KHÔNG import antd / react.
import { dangerCopy, DangerCopyView } from './workbenchView';

export type SplitMode = 'chapter' | 'range';

export const SPLIT_CARDS: Array<{ mode: SplitMode; title: string; hint: string }> = [
  { mode: 'chapter', title: 'Theo chương', hint: 'Mỗi tập gồm một hoặc nhiều chương nguyên văn (mặc định).' },
  { mode: 'range', title: 'Theo đoạn nguồn', hint: 'Cắt toàn văn thành các lát đoạn; một chương dài có thể thành nhiều tập. Tập đã viết/đã đẩy giữ nguyên, chỉ chia lại phần sau.' },
];
export const splitModeLabel = (m: SplitMode): string => (m === 'range' ? 'Theo đoạn nguồn' : 'Theo chương');

/** `outline.splitMode` / `outlineFlags.splitMode`: `'range'` ⇒ range, còn lại (rỗng, null) ⇒ chapter. */
export const currentSplitMode = (splitMode: string | null | undefined): SplitMode => (splitMode === 'range' ? 'range' : 'chapter');

export interface SplitCardState { enabled: boolean; reason?: string }

/**
 * Thẻ chế độ chia tập. 261015: thẻ "Theo đoạn nguồn" KHÔNG còn bị khoá khi truyện đã có kịch bản — BE chia nối đuôi (tập 1..M đã viết/đã đẩy giữ nguyên, chỉ chia M+1..N)
 * và báo 409 đồng bộ nếu không giữ nổi tiền tố. (Cũng không còn chốt "truyện thật".)
 */
export const splitCardState = (p: { card: SplitMode; busy: boolean; locked: boolean }): SplitCardState => {
  if (p.locked) return { enabled: false, reason: 'Bước đang khoá' };
  if (p.busy) return { enabled: false, reason: 'Đang có tác vụ chạy' };
  return { enabled: true };
};

export interface SplitRunState {
  label: 'Chia tập' | 'Chia lại tập'; enabled: boolean; reason?: string; confirm: DangerCopyView | null;
  /** true ⇒ chia lại MỘT PHẦN (có tập khoá M > 0, cả `chapter` lẫn `range` — BE khoá theo M chung): tập 1..M giữ nguyên — hộp xác nhận không được nói "toàn bộ truyện". */
  partial: boolean;
}

export const splitRunState = (p: {
  draftMode: SplitMode; current: SplitMode; hasEpisodes: boolean;
  /** M — số tập đã viết/đã đẩy lớn nhất (`lockedThroughOf`); 0 = chưa có. */
  lockedThrough: number; busy: boolean; locked: boolean;
  /** Tổng số tập khung xương (`outline.params.episodes`) — thiếu thì không kiểm "hết tập để chia". */
  total?: number;
}): SplitRunState => {
  const label = p.hasEpisodes ? 'Chia lại tập' : 'Chia tập';
  const M = p.lockedThrough;
  const partial = p.hasEpisodes && M > 0;   // 261015: chapter cũng khoá theo M chung (trước đây chỉ range)
  let confirm: DangerCopyView | null = null;
  if (p.hasEpisodes) {
    const base = dangerCopy('resplit');
    const extra: string[] = [];
    if (partial) {
      extra.push(`Tập 1–${M} (đã viết/đã đẩy) giữ nguyên nguyên văn; chia lại từ tập ${M + 1}.`);
      if (p.draftMode !== p.current) extra.push(`Đổi cách chia từ ${splitModeLabel(p.current)} sang ${splitModeLabel(p.draftMode)} cho phần chia lại.`);
    } else if (p.draftMode !== p.current) {
      extra.push(`Đổi cách chia từ ${splitModeLabel(p.current)} sang ${splitModeLabel(p.draftMode)} — mọi tập được chia lại.`);
    }
    confirm = { ...base, content: [base.content, ...extra].join(' ') };
  }
  if (p.locked) return { label, enabled: false, reason: 'Bước đang khoá', confirm, partial };
  if (p.busy) return { label, enabled: false, reason: 'Đang có tác vụ chạy', confirm, partial };
  if (M > 0 && p.total !== undefined && p.total <= M) {
    return { label, enabled: false, reason: `Khung xương chỉ có ${p.total} tập mà tập 1–${M} đã viết/đã đẩy — không còn tập nào để chia lại. Tăng số tập ở ② trước`, confirm, partial };
  }
  return { label, enabled: true, confirm, partial };
};

// ── 261005 E3-P2a — sửa tay ④: khoá phần đã viết · quyền nút · điểm cắt ứng viên quanh ranh giới ──────────────────

/** M — số tập lớn nhất đã có kịch bản (MỌI dòng, kể cả script rỗng/lỗi) hoặc đã đẩy sang Chapters. Cùng định nghĩa `episodeSplitLockedThrough` ở BE. */
export const lockedThroughOf = (
  episodes: ReadonlyArray<{ ep: number; handoff?: unknown; sourceChapterIds?: ReadonlyArray<string> }>,
  scripts: ReadonlyArray<{ episode: number }>,
): number => {
  let m = 0;
  for (const s of scripts) if (Number.isFinite(s.episode) && s.episode > m) m = s.episode;
  for (const e of episodes) if ((e.handoff || e.sourceChapterIds?.length) && e.ep > m) m = e.ep;
  return m;
};

export const LOCKED_TOOLTIP = 'Tập đã viết/đã đẩy — chỉ sửa tóm tắt/móc';

export interface ActionState { enabled: boolean; reason?: string }
export interface EpisodeActionStates { edit: ActionState; split: ActionState; merge: ActionState; move: ActionState }

/** Quyền nút ở từng dòng tập. Sửa chữ luôn bật (trừ đang bận); ba nút cấu trúc theo M / cờ stale / chế độ / vị trí. */
export const episodeActionStates = (p: {
  ep: number; total: number; lockedThrough: number; stale: boolean; busy: boolean; mode: SplitMode; chapterCount?: number;
}): EpisodeActionStates => {
  const busy: ActionState = { enabled: false, reason: 'Đang có tác vụ chạy' };
  if (p.busy) return { edit: busy, split: busy, merge: busy, move: busy };
  const edit: ActionState = { enabled: true };
  const gate = (): ActionState | null => {
    if (p.ep <= p.lockedThrough) return { enabled: false, reason: LOCKED_TOOLTIP };
    if (p.stale) return { enabled: false, reason: 'Danh sách tập đã cũ — bấm Chia lại tập trước khi tách/gộp/dời' };
    return null;
  };
  const g = gate();
  if (g) return { edit, split: g, merge: g, move: g };
  const last: ActionState = { enabled: false, reason: 'Tập cuối không có tập sau' };
  const split: ActionState = p.mode === 'chapter' && (p.chapterCount ?? 0) < 2 ? { enabled: false, reason: 'Tập chỉ có 1 chương — không tách được theo chương' } : { enabled: true };
  const isLast = p.ep >= p.total;
  return { edit, split, merge: isLast ? last : { enabled: true }, move: isLast ? last : { enabled: true } };
};

/** `chapter` — chương nào của tập có thể là chương CUỐI của nửa đầu khi tách (mọi chương trừ chương cuối). */
export const chapterSplitChoices = (sourceChapters: ReadonlyArray<number> | undefined): number[] => {
  const c = Array.from(new Set(sourceChapters ?? [])).sort((a, b) => a - b);
  return c.slice(0, -1);
};

export interface ChapterMoveChoice { direction: 'toNext' | 'toPrev'; chapter: number; label: string; enabled: boolean; reason?: string }
/** `chapter` — dời ranh giới = chuyển một chương giữa hai tập kề. Nhường chương phải còn ≥ 1 chương. */
export const chapterMoveChoices = (cur: ReadonlyArray<number> | undefined, next: ReadonlyArray<number> | undefined): ChapterMoveChoice[] => {
  const a = Array.from(new Set(cur ?? [])).sort((x, y) => x - y);
  const b = Array.from(new Set(next ?? [])).sort((x, y) => x - y);
  const out: ChapterMoveChoice[] = [];
  if (a.length) out.push({ direction: 'toNext', chapter: a[a.length - 1], label: `Chuyển chương ${a[a.length - 1]} sang tập sau`, enabled: a.length >= 2, ...(a.length >= 2 ? {} : { reason: 'Tập chỉ có 1 chương — dùng Gộp' }) });
  if (b.length) out.push({ direction: 'toPrev', chapter: b[0], label: `Chuyển chương ${b[0]} về tập này`, enabled: b.length >= 2, ...(b.length >= 2 ? {} : { reason: 'Tập sau chỉ có 1 chương — dùng Gộp' }) });
  return out;
};

export interface CutOption { offset: number; tier: 1 | 2; chapterEnd: boolean; label: string }

/** Nhãn một điểm cắt: ~`w` ký tự trước ▮ ~`w` ký tự sau (gộp khoảng trắng, cắt chữ ở biên). */
export const cutLabel = (content: string, offset: number, w = 40): string => {
  const flat = (s: string) => s.replace(/\s+/g, ' ');
  const before = flat(content.slice(Math.max(0, offset - w * 2), offset)).trim().slice(-w);
  const after = flat(content.slice(offset, offset + w * 2)).trim().slice(0, w);
  return `${before.length >= w ? '…' : ''}${before} ▮ ${after}${after.length >= w ? '…' : ''}`;
};

/**
 * Chọn ≤ `perSide` điểm cắt mỗi phía của `pivot`, chỉ trong cửa sổ `(min, max)` (phần chương thuộc tập đang xét). Không trả chính `pivot`.
 * Ứng viên đã đến từ BE (`split-candidates`); hàm này chỉ lọc + gắn nhãn.
 */
export const nearbyCuts = (
  content: string, candidates: ReadonlyArray<{ offset: number; tier: 1 | 2; chapterEnd?: boolean }>, pivot: number, window: { min: number; max: number }, perSide = 10,
): { before: CutOption[]; after: CutOption[] } => {
  const inside = candidates.filter((c) => c.offset > window.min && c.offset < window.max + (c.chapterEnd ? 1 : 0) && c.offset !== pivot);
  const mk = (c: { offset: number; tier: 1 | 2; chapterEnd?: boolean }): CutOption => ({ offset: c.offset, tier: c.tier, chapterEnd: !!c.chapterEnd, label: c.chapterEnd ? 'Cuối chương' : cutLabel(content, c.offset) });
  const before = inside.filter((c) => c.offset < pivot).slice(-perSide).map(mk);
  const after = inside.filter((c) => c.offset > pivot).slice(0, perSide).map(mk);
  return { before, after };
};

/** Độ dài nguồn hiển thị; thiếu offset thì vẫn nói rõ số chương, không để dấu `—` mơ hồ. */
export const formatSourceLength = (chars: number | undefined, sourceChapters: readonly number[] = []): string =>
  chars === undefined
    ? (sourceChapters.length ? `${sourceChapters.length} chương nguồn` : 'chưa đo được độ dài nguồn')
    : `${chars.toLocaleString('vi-VN')} ký tự`;

/** Gom lý do từ chối cho người đọc (một dòng mỗi lệnh). */
export const describeSplitRejected = (rejected: ReadonlyArray<{ code: string; reason: string }>): string =>
  rejected.map((r) => r.reason).join(' · ');

export interface RewriteActionState { enabled: boolean; reason?: string }

/**
 * 261005 E3-P2b — nút "Nhờ AI viết lại" (ở nhãn "Tóm tắt cần xem lại"): tập ≤ M bị khoá (chỉ sửa chữ), danh sách tập lỗi thời ⇒ chia lại trước, bận ⇒ chờ.
 * Server vẫn là cổng thật (400 INVALID_EPISODES · 409 EPISODES_STALE).
 */
export const rewriteActionState = (p: { eps: ReadonlyArray<number>; lockedThrough: number; stale: boolean; busy: boolean; starting: boolean }): RewriteActionState => {
  if (!p.eps.length) return { enabled: false, reason: 'Không có tập nào cần viết lại' };
  if (p.eps.some((e) => e <= p.lockedThrough)) return { enabled: false, reason: 'Tập đã viết/đã đẩy — chỉ sửa chữ, không để AI viết lại' };
  if (p.stale) return { enabled: false, reason: 'Danh sách tập đã cũ — bấm Chia lại tập trước' };
  if (p.busy || p.starting) return { enabled: false, reason: 'Đang có tác vụ chạy' };
  return { enabled: true };
};
